import React, { useState, useEffect, useCallback } from 'react';
import { maintenancePlansAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AppDrawer } from '../components/ui/AppDrawer';
import {
  Plus, Loader2, Calendar, DollarSign, Wrench, Pause, XCircle, Play, Shield, ArrowUpRight, Clock, Edit2
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-50 text-amber-700 border-amber-200',
  canceled: 'bg-stone-100 text-stone-500 border-stone-200',
};

const SOURCE_LABELS = { manual: 'Manual', upsell: 'Upsell', import: 'Import' };

const FREQ_LABELS = {
  monthly: '/mo', quarterly: '/qtr', semi_annual: '/6mo', annual: '/yr',
};

const formatTime12h = (hhmm) => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

export default function CustomerMaintenancePlans({ customerId }) {
  const [enrollments, setEnrollments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [enrollNotes, setEnrollNotes] = useState('');
  const [timeOverride, setTimeOverride] = useState('');
  const [editTimeId, setEditTimeId] = useState(null);
  const [editTimeValue, setEditTimeValue] = useState('');
  const [editTimeSaving, setEditTimeSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [enrollRes, plansRes] = await Promise.all([
        maintenancePlansAPI.customerEnrollments(customerId),
        maintenancePlansAPI.list(true),
      ]);
      setEnrollments(enrollRes.data.enrollments);
      setPlans(plansRes.data.plans);
    } catch { toast.error('Failed to load maintenance plans'); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const handleEnroll = async () => {
    if (!selectedPlanId) return toast.error('Select a plan');
    if (timeOverride && !/^([01]\d|2[0-3]):([0-5]\d)$/.test(timeOverride)) {
      return toast.error('Preferred time must be HH:MM');
    }
    setSaving(true);
    try {
      await maintenancePlansAPI.enrollCustomer(customerId, {
        plan_id: selectedPlanId,
        notes: enrollNotes.trim() || null,
        scheduled_time_override: timeOverride || null,
      });
      toast.success('Customer enrolled in plan');
      setDrawerOpen(false);
      setSelectedPlanId('');
      setEnrollNotes('');
      setTimeOverride('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll customer');
    } finally { setSaving(false); }
  };

  const beginEditTime = (enrollment) => {
    setEditTimeId(enrollment.id);
    setEditTimeValue(enrollment.scheduled_time_override || '');
  };

  const cancelEditTime = () => {
    setEditTimeId(null);
    setEditTimeValue('');
  };

  const saveEditTime = async (enrollmentId) => {
    const trimmed = (editTimeValue || '').trim();
    if (trimmed && !/^([01]\d|2[0-3]):([0-5]\d)$/.test(trimmed)) {
      return toast.error('Preferred time must be HH:MM');
    }
    setEditTimeSaving(true);
    try {
      // Empty string = clear override (fall back to plan default)
      await maintenancePlansAPI.updateEnrollment(enrollmentId, {
        scheduled_time_override: trimmed,
      });
      toast.success(trimmed ? 'Preferred time updated — future jobs rescheduled' : 'Cleared — future jobs use plan default');
      setEditTimeId(null);
      setEditTimeValue('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update time');
    } finally { setEditTimeSaving(false); }
  };

  const handleAction = async (enrollmentId, newStatus) => {
    if (newStatus === 'canceled' && !window.confirm('Cancel this enrollment? No further invoices or jobs will be generated.')) return;
    try {
      await maintenancePlansAPI.updateEnrollment(enrollmentId, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Enrollment resumed' : newStatus === 'paused' ? 'Enrollment paused' : 'Enrollment canceled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  return (
    <div className="space-y-4" data-testid="customer-maintenance-plans">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-700">Maintenance Plans</h3>
        <Button size="sm" variant="outline" onClick={() => setDrawerOpen(true)} data-testid="enroll-customer-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Plan
        </Button>
      </div>

      {enrollments.length === 0 ? (
        <div className="text-center py-6 text-sm text-stone-400">
          <Shield className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          No active maintenance plans
        </div>
      ) : (
        <div className="space-y-3">
          {enrollments.map(e => {
            const startFormatted = e.start_date ? new Date(e.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
            const billFormatted = e.next_billing_date ? new Date(e.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
            const svcFormatted = e.next_service_date ? new Date(e.next_service_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
            return (
            <Card key={e.id} className="overflow-hidden hover:shadow-sm transition-shadow" data-testid={`customer-enrollment-${e.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-semibold text-sm text-stone-900">{e.plan_name}</span>
                      <Badge variant="outline" className={`text-xs ${STATUS_STYLES[e.status]}`}>{e.status}</Badge>
                      {e.source && (
                        <Badge variant="outline" className={`text-xs ${e.source === 'upsell' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-stone-50 text-stone-500 border-stone-200'}`}>
                          {e.source === 'upsell' && <ArrowUpRight className="w-3 h-3 mr-0.5" />}
                          {SOURCE_LABELS[e.source] || e.source}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-stone-500">
                      <span className="font-medium text-stone-600">${e.enrolled_price.toFixed(2)}</span>
                      {startFormatted && <span className="text-stone-400">Since {startFormatted}</span>}
                      {e.status !== 'canceled' && (billFormatted || svcFormatted) && <span className="text-stone-300">|</span>}
                      {billFormatted && e.status !== 'canceled' && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-stone-400" />Next bill: {billFormatted}</span>
                      )}
                      {billFormatted && svcFormatted && e.status !== 'canceled' && <span className="text-stone-300">&bull;</span>}
                      {svcFormatted && e.status !== 'canceled' && (
                        <span className="flex items-center gap-1"><Wrench className="w-3 h-3 text-stone-400" />Next service: {svcFormatted}</span>
                      )}
                    </div>
                    {e.notes && <p className="text-xs text-stone-400 mt-1.5 italic">{e.notes}</p>}
                    {/* Scheduled time row — shows effective time + inline edit */}
                    {e.status !== 'canceled' && (
                      <div className="flex items-center gap-2 mt-2 text-xs" data-testid={`enrollment-time-row-${e.id}`}>
                        <Clock className="w-3.5 h-3.5 text-stone-400" />
                        {editTimeId === e.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="time"
                              step="300"
                              value={editTimeValue}
                              onChange={ev => setEditTimeValue(ev.target.value)}
                              className="h-7 w-28 text-xs"
                              data-testid={`enrollment-time-input-${e.id}`}
                            />
                            <Button
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => saveEditTime(e.id)}
                              disabled={editTimeSaving}
                              data-testid={`enrollment-time-save-${e.id}`}
                            >
                              {editTimeSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-stone-500"
                              onClick={cancelEditTime}
                              disabled={editTimeSaving}
                            >
                              Cancel
                            </Button>
                            {e.scheduled_time_override && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs text-stone-500 hover:text-red-500"
                                onClick={() => { setEditTimeValue(''); saveEditTime(e.id); }}
                                disabled={editTimeSaving}
                                data-testid={`enrollment-time-clear-${e.id}`}
                                title="Clear override and use plan default"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                        ) : (
                          <>
                            <span className="text-stone-600 font-medium" data-testid={`enrollment-time-value-${e.id}`}>
                              {formatTime12h(e.effective_scheduled_time) || '—'}
                            </span>
                            {e.scheduled_time_override ? (
                              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 h-4 px-1.5">
                                Override
                              </Badge>
                            ) : (
                              <span className="text-stone-400">(plan default)</span>
                            )}
                            {e.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-stone-400 hover:text-stone-700"
                                onClick={() => beginEditTime(e)}
                                data-testid={`enrollment-time-edit-${e.id}`}
                                title="Change preferred time"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {e.status === 'active' && (
                      <>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50" onClick={() => handleAction(e.id, 'paused')} title="Pause" data-testid={`pause-${e.id}`}>
                          <Pause className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleAction(e.id, 'canceled')} title="Cancel" data-testid={`cancel-${e.id}`}>
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    {e.status === 'paused' && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50" onClick={() => handleAction(e.id, 'active')} title="Resume" data-testid={`resume-${e.id}`}>
                        <Play className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <AppDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title="Enroll in Maintenance Plan">
        <div className="space-y-4 p-1">
          <div className="space-y-2">
            <Label>Select Plan</Label>
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger data-testid="select-plan-dropdown"><SelectValue placeholder="Choose a plan..." /></SelectTrigger>
              <SelectContent>
                {plans.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — ${p.price.toFixed(2)}{FREQ_LABELS[p.billing_frequency] || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPlan && (
            <div className="rounded-lg bg-stone-50 p-3 text-sm space-y-1">
              <p className="font-medium text-stone-700">{selectedPlan.name}</p>
              {selectedPlan.description && <p className="text-stone-500 text-xs">{selectedPlan.description}</p>}
              <div className="flex gap-3 text-xs text-stone-500 pt-1">
                <span>${selectedPlan.price.toFixed(2)} / {selectedPlan.billing_frequency.replace('_', ' ')}</span>
                <span>{selectedPlan.visits_per_year} visit{selectedPlan.visits_per_year !== 1 ? 's' : ''}/year</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime12h(selectedPlan.default_scheduled_time) || '8:00 AM'}
                </span>
              </div>
              <p className="text-xs text-stone-400 pt-1">Price locked at ${selectedPlan.price.toFixed(2)} at enrollment. First invoice generated immediately.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Preferred Scheduled Time (optional)</Label>
            <Input
              type="time"
              step="300"
              value={timeOverride}
              onChange={e => setTimeOverride(e.target.value)}
              data-testid="enroll-time-override-input"
            />
            <p className="text-xs text-muted-foreground">
              {timeOverride ? (
                <>This customer's visits will be scheduled at <span className="font-medium text-foreground">{formatTime12h(timeOverride)}</span></>
              ) : selectedPlan ? (
                <>Leave blank to use the plan default: <span className="font-medium text-foreground">{formatTime12h(selectedPlan.default_scheduled_time) || '8:00 AM'}</span></>
              ) : (
                <>Leave blank to use the plan's default scheduled time</>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea value={enrollNotes} onChange={e => setEnrollNotes(e.target.value)} placeholder="Enrolled during spring promotion..." rows={2} data-testid="enroll-notes-input" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button onClick={handleEnroll} disabled={saving || !selectedPlanId} data-testid="confirm-enroll-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enroll Customer
            </Button>
          </div>
        </div>
      </AppDrawer>
    </div>
  );
}
